import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { cartCheckOut, getCart } from "@/queries/cart"
import type { CartItem } from "@/types/shop"
import { useMutation } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import { createFileRoute } from "@tanstack/react-router"
import { ArrowRight, BanknoteIcon, CreditCardIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { format } from "date-fns-jalali" // برای تقویم شمسی
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import { useStore } from "@tanstack/react-store"
import userStore from "@/stores/user-store"

import { AxiosError } from "axios"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import clsx from "clsx"
import {
  MellatColorIcon,
  SamanColorIcon,
} from "@snapp-store/iranian-banks-react-icons"

export const Route = createFileRoute("/shop/checkout/")({
  component: CheckoutPage,
})

function CheckoutPage() {
  const [showBank, setShowBank] = useState(false)
  const [cart, setCart] = useState<Array<CartItem>>([])
  const [delivery, setDelivery] = useState<any>({})
  const { balance } = useStore(userStore)
  const [details, setCartDetails] = useState<null | {
    finalPrice: number
    totalDiscount: number
    price: number
    totalSale?: number
    finalPriceWithDelivery: number,
    deliveryPrice?:number
  }>(null)
  const navigate = useNavigate()
  const [paymentType, setPaymentType] = useState("wallet")
  const [bank, setBank] = useState("saman")
  const samanForm = useRef<null | HTMLFormElement>(null)
  const mellatFrom = useRef<null | HTMLFormElement>(null)
  const [formData, setFormData] = useState<{ token: string }>({ token: "" })

  const getCartMutation = useMutation({
    mutationFn: () => getCart(),
    mutationKey: ["shoping-cart"],
  })

  const cartCheckOutMutation = useMutation({
    mutationFn: (data?: any) => cartCheckOut(data),
    mutationKey: ["checkout"],
    onSuccess: (data) => {
      if (paymentType === "wallet") {
        toast.success("سفارش شما با موفقیت ثبت شد")
        navigate({ to: "/shop" })
      } else if (paymentType === "bank") {
        setFormData({ ...data.data })
        if (bank === "saman") {
          setTimeout(() => {
            samanForm.current?.submit()
          }, 500)
        } else if (bank === "mellat") {
          setTimeout(() => {
            mellatFrom.current?.submit()
          }, 1000)
        }
      }
    },
    onError(error: AxiosError<{ message?: string }>) {
      if (
        error?.response?.data?.message?.includes(
          "There is not enough stock available for product"
        )
      ) {
        toast.error("محصول کافی برای این سبد خرید موجود نمی باشد", {
          action: (
            <>
              <Link to="/shop/cart">
                <Button>رفتن به سبد خرید</Button>
              </Link>
            </>
          ),
          duration: 50000,
        })
      }
      // toast.error('خطایی در خرید رخ داده است')
    },
  })

  useEffect(() => {
    getCartMutation.mutate(undefined, {
      onSuccess: (data) => {
        const delivery = data.data.cart.delivery
        const cart = data.data.cart.items
        setCart(Object.values(cart))
        setCartDetails(data.data.cart.details)
        setDelivery({
          ...delivery,
        })
      },
    })
  }, [])

  return (
    <>
      <div className="flex items-center py-10">
        <Link to="/shop/logistic">
          <Button variant="ghost" size="icon">
            <ArrowRight />
          </Button>
        </Link>
        <h1 className="text-xl font-bold">پرداخت و نهایی سازی سفارش</h1>
      </div>

      <form
        ref={samanForm}
        action="https://sep.shaparak.ir/OnlinePG/OnlinePG"
        id="samanBank"
        method="POST"
      >
        <input name="Token" type="hidden" value={formData.token} />
        <input type="hidden" name="GetMethod" value="" />
      </form>

      <form
        action="https://bpm.shaparak.ir/pgwchannel/startpay.mellat"
        method="POST"
        id="mellatBank"
        ref={mellatFrom}
      >
        <input type="hidden" id="RefId" name="RefId" value={formData.token} />
        {/* <input type="text" id="MobileNo" name="MobileNo" value='989121231111' /> */}
        {/* <input type='submit' value='Submit' className='hidden' /> */}
      </form>

      <Dialog open={showBank} onOpenChange={(open) => setShowBank(open)}>
        <DialogContent
          className="sm:max-w-[425px]"
          aria-description="Modal For Not Money"
        >
          <DialogHeader>
            <DialogTitle>اعتبار شما جهت خرید کافی نمی باشد</DialogTitle>
          </DialogHeader>
          <div>
            <p>
              اعتبار شما برای پرداخت کافی نمی باشد ابتدا اعتبار خود را افزایش
              دهید
            </p>
          </div>
          <DialogFooter>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setShowBank(false)}>
                لغو
              </Button>
              <Link to="/payment/bank">
                <Button autoFocus>افزایش اعتبار</Button>
              </Link>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {getCartMutation?.isPending ? (
        <>
          <Skeleton className="max-w-lg w-full h-80 mx-auto" />
        </>
      ) : (
        <Card className="max-w-lg mx-auto">
          <CardHeader className="flex justify-between">
            <CardTitle>خلاصه سفارش</CardTitle>
            <span className="text-muted-foreground">{cart.length} کالا</span>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2">
            <p className="">جمع کالا ها: </p>
            <p className="text-end">
              {Intl.NumberFormat().format(details?.price || 0)} ریال
            </p>
            {details?.totalDiscount && details.totalDiscount > 0 ? (
              <>
                <p>مجموع تخفیف:</p>
                <p className="text-end">
                  {Intl.NumberFormat().format(details.totalDiscount || 0)} ریال
                </p>
              </>
            ) : (
              ""
            )}
            <p>هزینه ارسال</p>
            <p className="text-end">
              {Intl.NumberFormat().format(details?.deliveryPrice || 0)} ریال
            </p>
            <Separator className="col-span-2" />
            <p>مجموع : </p>
            {details?.finalPrice ? (
              <p className="text-end">
                {Intl.NumberFormat().format(
                  details?.finalPriceWithDelivery || 0
                )}{" "}
                ریال
              </p>
            ) : (
              ""
            )}
            <Separator className="col-span-2" />
            <p className="col-span-2">آدرس ارسال :</p>
            <p className="col-span-2">
              {" "}
              {delivery.province} / {delivery.city} / {delivery.address}
            </p>

            <p>تاریخ و ساعت ارسال : </p>
            <div className="text-end flex flex-col">
              {delivery?.date && (
                <span>
                  {format(new Date(`${delivery.date}T00:00:00`), "yyyy/MM/dd")}
                </span>
              )}
              <span>
                {`${delivery.deliveryBetweenEnd}:00`} الی{" "}
                {`${delivery.deliveryBetweenStart}:00`}
              </span>
            </div>

            <Separator className="col-span-2" />
            <div className="space-y-2 col-span-2">
              <p className="font-bold">نحوه پرداخت: </p>
              <RadioGroup
                value={paymentType}
                onValueChange={setPaymentType}
                className="flex"
              >
                <Label
                  htmlFor="wallet"
                  className={clsx(
                    "flex items-center gap-2 rounded-md border p-3 cursor-pointer w-full",
                    paymentType === "wallet" ? "border-primary" : "border"
                  )}
                >
                  <RadioGroupItem value="wallet" id="wallet" />
                  <CreditCardIcon />
                  <Label htmlFor="wallet" className="flex gap-2 w-full">
                    کیف پول
                  </Label>
                </Label>

                <Label
                  htmlFor="bank"
                  className={clsx(
                    "flex items-center gap-2 rounded-md border p-3 cursor-pointer w-full",
                    paymentType === "bank" ? "border-primary" : "border"
                  )}
                >
                  <RadioGroupItem value="bank" id="bank" />
                  <BanknoteIcon />
                  <Label htmlFor="bank" className="flex gap-2 w-full">
                    بانک
                  </Label>
                </Label>
              </RadioGroup>

              {/* اگر بانک انتخاب شد */}
              {paymentType === "bank" && (
                <>
                  <p className="font-bold mb-2">یک بانک انتخاب کنید : </p>
                  <RadioGroup
                    value={bank}
                    onValueChange={setBank}
                    className="flex"
                  >
                    <Label 
                      htmlFor="saman"
                      className={clsx(
                        "flex items-center gap-2 rounded-md border p-3 cursor-pointer w-full",
                        bank === "saman" ? "border-primary" : "border"
                      )}
                    >
                      <RadioGroupItem value="saman" id="saman" />
                      <SamanColorIcon />
                      <p>سامان</p>
                    </Label>

                    <Label
                      htmlFor="mellat"
                      className={clsx(
                        "flex items-center gap-2 rounded-md border p-3 cursor-pointer w-full",
                        bank === "mellat" ? "border-primary" : "border"
                      )}
                    >
                      <RadioGroupItem value="mellat" id="mellat" />
                      <MellatColorIcon />
                      <p>ملت</p>
                    </Label>
                  </RadioGroup>
                </>
              )}
            </div>
            <div className="col-span-2">
              <Button
                className="w-full"
                disabled={cartCheckOutMutation.isPending}
                onClick={() => {
                  if (
                    paymentType === "wallet" &&
                    details?.finalPriceWithDelivery
                  ) {
                    if (details?.finalPriceWithDelivery > balance) {
                      setShowBank(true)
                      toast.error("اعتبار شما جهت خرید کافی نمی باشد")
                    } else {
                      cartCheckOutMutation.mutate({})
                    }
                  } else if (paymentType === "bank") {
                    const returnUrl = `${window.location.protocol}//${window.location.host}/shop/payment/verify/`
                    cartCheckOutMutation.mutate({
                      bank: bank,
                      returnUrl,
                    })
                  }
                }}
              >
                پرداخت
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  )
}
